// playbeacon.ts — the community mission-pack PLAY beacon (community-content-plan §4.6 / M2).
//
// When a COMMUNITY pack's single-player mission starts, the client fires one lightweight,
// fire-and-forget POST to the wi-api /api/playpack endpoint so the pack's `plays` counter
// and the pack_stats day bucket advance (feeding the "Popular this month" feed). Built-in
// packs (the shipped campaign, PACKID_MAIN) and the fake Custom-URL row never count.
//
// Kept as a pure-ish module (the decision + body build are pure; the send is a thin fetch
// wrapper) so the logic is unit-testable without booting main.ts.

/** the shipped campaign pack id (game/dlmissionpack — PackId {dword id}); never counted. */
export const PACKID_MAIN = 0xffffffff;
/** the fake "Custom URL…" catalog row id; never counted. */
export const PACKID_CUSTOMURL = 0xfefefefe;

/** the production play-beacon endpoint (the wi-api worker; POST /api/playpack). */
export const kszPlayPackUrl = 'https://api.warfareincorporated.com/api/playpack';

/** True when a pack id denotes a COMMUNITY pack (a real minted id, not a built-in/reserved
 *  one) — i.e. a mission start that should count as a play. */
export function isCommunityPack(packId: number): boolean {
  const id = packId >>> 0;
  return id !== 0 && id !== PACKID_MAIN && id !== PACKID_CUSTOMURL;
}

/** The play-beacon POST body: the pack id + whatever identity is available (a signed-in
 *  token wins server-side; else the anonymous device id). Both identity fields are optional
 *  strings — the server rejects a body with neither, which the fire-and-forget send ignores. */
export function playBeaconBody(
  packId: number,
  identity: { did?: string; token?: string },
): { id: number; did: string; token: string } {
  return {
    id: packId >>> 0,
    did: identity.did ?? '',
    token: identity.token ?? '',
  };
}

export interface PlayBeaconOpts {
  /** the device id (kszPrefDeviceId) — the anonymous identity. */
  did?: string;
  /** a signed-in AUTH_GOOD token (kszPrefToken), if the player is non-anonymous. */
  token?: string;
  /** master gate — pass the host's telemetry-enabled predicate (prod-host only), so a
   *  dev/localhost/headless run never inflates the counts. */
  enabled: boolean;
  /** the endpoint (defaults to {@link kszPlayPackUrl}); overridable for tests/local stacks. */
  url?: string;
  /** the fetch to use (defaults to globalThis.fetch); injectable for tests. */
  fetchImpl?: typeof fetch;
}

/** Fire the play beacon for a community pack mission start. No-op (returns false) for
 *  built-in packs, when disabled, when no identity is available, or when fetch is absent.
 *  Never throws and never awaits — telemetry must never disrupt gameplay. Returns whether
 *  a request was actually dispatched (for tests). */
export function sendPlayBeacon(packId: number, opts: PlayBeaconOpts): boolean {
  if (!opts.enabled) return false;
  if (!isCommunityPack(packId)) return false;
  const did = opts.did ?? '';
  const token = opts.token ?? '';
  if (did === '' && token === '') return false; // the server needs an identity
  const f = opts.fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined);
  if (f === undefined) return false;
  try {
    void f(opts.url ?? kszPlayPackUrl, {
      method: 'POST',
      keepalive: true, // still flushes if the mission start races a navigation
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(playBeaconBody(packId, { did, token })),
    }).catch(() => {
      /* telemetry must never surface to the player */
    });
  } catch {
    /* fetch can throw synchronously on some bad inputs — swallow */
    return false;
  }
  return true;
}
